import React, {useState} from 'react';
import {Alert, Box, Button} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import SampleBulkUpload from './SampleBulkUpload';
import {ExperimentDesign} from './ExperimentDesign';
import Step3HasSamples from './Step3HasSamples';
import {filterInvalidOrUnusedFilenames} from './ParseCsv';
import {Error} from '../../../../components/Error';

interface PageState {
    /**
     * Holds contents of the CSV file. In the RNA case, the deduplicated
     * information, without the filenames. In the ChIP case, the metadata CSV.
     */
    parsedSamples: Record<string, string>[];
    /**
     * Holds the mapping from sample names to filenames. In the RNA case, the
     * information extracted from the CSV. In the ChIP case, the filenames CSV.
     */
    parsedFilenames: Record<string, string[]>;
    fastq_files: File[];
}

interface Step3IndexProps {
    experiment: any;
    createSamples: (
        userinputSamples: any[],
        samplenameToValidFiles: Record<string, File[]>,
        samplenameToExpectedFilenames?: Record<string, string[]>
    ) => Promise<void>;
    samples: any[];
    setStep: (step: number) => void;
}

/**
 * Step 3 of the experiment creation process.
 *
 * - Shows the user what CSV files are expected and lets them upload theirs.
 * - Lets the user upload FASTQ files.
 */
const Step3Index = ({experiment, createSamples, samples, setStep}: Step3IndexProps) => {
    const navigate = useNavigate();
    const [showUploader, setShowUploader] = useState(false);
    const [csvError, setCsvError] = useState('');
    const [pageState, setPageState] = useState<PageState>({
        parsedSamples: [],
        parsedFilenames: {},
        fastq_files: []
    });

    const complete = Boolean(pageState.parsedSamples.length > 0 && pageState.fastq_files.length > 0 && csvError === '');
    const uploadEnabled = Boolean(csvError === '' && Object.keys(pageState.parsedFilenames).length > 0);
    const csvFilenames = new Set(Object.values(pageState.parsedFilenames).flat());
    const unmatchedFastqNames = pageState.fastq_files
        .map(file => file.name)
        .filter(fileName => csvFilenames.size > 0 && !csvFilenames.has(fileName));

    /**
     * Removes a FastQ file from the page state.
     */
    const handleRemoveFile = (fileName: string) => {
        setPageState((state: any) => ({
            ...state,
            fastq_files: state.fastq_files.filter((file: any) => file.name !== fileName)
        }));
    };

    const setParsedSamples = (parsedSamples: Record<string, string>[]) => {
        setPageState((state: PageState) => ({...state, parsedSamples}));
    };
    const setParsedFilenames = (parsedFilenames: Record<string, string[]>) => {
        setPageState((state: PageState) => ({...state, parsedFilenames}));
    };

    /**
     * Adds the selected files to the page state.
     */
    const handleFASTQUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const filesList = event.target.files;
        if (!filesList || filesList?.length === 0) return;
        let files: File[] = [];
        for (let i = 0; i < filesList.length; i++) {
            files.push(filesList[i]);
        }
        // TODO Can user select same file twice?
        // TODO What if user selects file with same name, but from different folder/content?
        setPageState((state: any) => ({...state, fastq_files: [...state.fastq_files, ...files]}));
    };

    /**
     * Handles the "Save and Continue" button click.
     *
     * Links samples' filenames to the File objects. Passes the samples and the
     * mapping from sample to File objects up to the parent through
     * createSamples
     */
    const handleSaveAndContinue = () => {
        const {parsedSamples, parsedFilenames, fastq_files} = pageState;

        if (!parsedSamples.length) {
            console.error('CSV file empty');
            return;
        }

        const sampleNameToValidFiles = filterInvalidOrUnusedFilenames(parsedFilenames, fastq_files, experiment?.sequencing_read_type);

        createSamples(parsedSamples, sampleNameToValidFiles, parsedFilenames);
    };

    return (
        <Box>
            {experiment?.sample_count > 0 && samples?.length && !showUploader ? (
                <Step3HasSamples samples={samples} setStep={setStep} setShowUploader={setShowUploader} />
            ) : (
                <>
                    <Box>
                        <ExperimentDesign
                            sequencingType={experiment?.sequencing_type}
                            sequencingReadType={experiment?.sequencing_read_type as 'paired' | 'singled'}
                            filesPerSample={Number(experiment?.files_per_sample)}
                            gotCsvDesign={pageState.parsedSamples.length > 0}
                            parsedSamples={pageState.parsedSamples}
                            parsedFilenames={pageState.parsedFilenames}
                            setParsedSamples={setParsedSamples}
                            setSamplesToFilenames={setParsedFilenames}
                            setCsvError={setCsvError}
                        />
                        <SampleBulkUpload
                            selectedFastqFiles={pageState?.fastq_files}
                            handleRemoveFile={handleRemoveFile}
                            handleFASTQUpload={handleFASTQUpload}
                            isUploadEnabled={uploadEnabled}
                        />
                        {unmatchedFastqNames.length > 0 && (
                            <Alert severity="warning" sx={{mt: 2}}>
                                {unmatchedFastqNames.length} uploaded FASTQ {unmatchedFastqNames.length > 1 ? 'files are' : 'file is'} not
                                listed in your filenames CSV and will be ignored: {unmatchedFastqNames.join(', ')}
                            </Alert>
                        )}
                    </Box>
                    {csvError && <Error message={'CSV error: ' + csvError} />}
                    <Button variant="contained" color="primary" onClick={handleSaveAndContinue} disabled={!complete} fullWidth>
                        Save and Continue
                    </Button>
                    <Button variant="text" color="primary" onClick={() => navigate(`/`)} fullWidth>
                        Save Progress and Exit
                    </Button>
                </>
            )}
        </Box>
    );
};

export default Step3Index;
